import {
  JsonSchemaProperty,
  PRIMARY_TYPE_OPTIONS,
  PrimaryType,
  ProvidedSchema,
  UIColumn
} from './types'
import { JSONSchema } from '../dto/template'

import { v4 as uuid } from 'uuid'
import { snake } from '../../../utils/snake'

export const initialSchema: ProvidedSchema | null = {
  items: [
    {
      type: 'object',
      required: [],
      properties: {}
    }
  ]
}

const getSchemaItems = (
  schema: JSONSchema,
  fieldName?: string
): JSONSchema['items'] => {
  if (fieldName && schema?.properties?.[fieldName]) {
    return schema.properties[fieldName].items as JSONSchema['items']
  }
  return schema?.items ?? []
}

export const getSchemaRequired = (
  schema: JSONSchema,
  fieldName?: string
): string[] => getSchemaItems(schema, fieldName)?.[0]?.required ?? []

export const toProvidedSchema = (
  columns: JsonSchemaProperty[],
  required: string[],
  fieldName: string
): JSONSchema => {
  const normalizedRequired = required.filter(req =>
    columns.some(col => col.key === req)
  )

  const properties = columns.reduce<Record<string, JsonSchemaProperty>>(
    (acc, val) => {
      acc[val.key] = {
        key: val.key,
        type: (val.type && val.type.length ? val.type : ['string', 'null']) as (
          | 'string'
          | 'null'
        )[],
        title: val.title || val.key
      }
      return acc
    },
    {}
  )

  return {
    type: 'object',
    $schema: 'http://json-schema.org/draft-04/schema#',
    properties: {
      [fieldName]: {
        type: ['array', 'null'],
        items: [
          {
            type: 'object',
            required: normalizedRequired,
            properties
          }
        ]
      }
    }
  }
}

export const mapSchemaToUIColumns = (
  schema: JSONSchema,
  fieldName?: string
): UIColumn[] => {
  const item = getSchemaItems(schema, fieldName)?.[0]
  const props = item?.properties ?? {}

  return Object.keys(props).map(propName => {
    const prop = props[propName] as {
      key: string
      title: string
      type: string[]
    }

    const safeKey = snake(prop?.key || propName) || propName

    const primary =
      (Array.isArray(prop?.type)
        ? prop.type.find(type =>
            (PRIMARY_TYPE_OPTIONS as readonly string[]).includes(type)
          )
        : undefined) || 'string'

    const finalTypes: Array<PrimaryType | 'null'> = [
      primary as PrimaryType,
      'null'
    ]

    return {
      uiId: uuid(),
      key: safeKey,
      title: prop?.title || safeKey,
      type: finalTypes as unknown as ('string' | 'null')[] | string[]
    }
  })
}
